import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TEAM_STRINGS } from '@/lib/constants/team-strings';
import { getApiErrorCode, getApiErrorDetail } from '@/lib/utils/error.utils';
import { useInviteMember } from '../hooks/useInviteMember';
import {
  inviteMemberFormSchema,
  toInviteSectorId,
  type InviteMemberFormValues,
} from '../invite-member-schema';
import { InviteSectorField } from './InviteSectorField';

interface InviteMemberDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
  /**
   * Shows the optional sector pick (MYN-202). Driven by
   * `TEAM_PAGE_FEATURES[entityType].inviteSector` in the page, never by an
   * entity-type check here.
   */
  showSector?: boolean;
}

const GENERIC_ERROR = 'Could not send the invite. Please try again.';
const SECTOR_ERRORS = TEAM_STRINGS.INVITE_SECTOR.ERRORS;

/**
 * "Invite a Team Member" modal (MYN-43). Composes the shared Dialog. The email
 * domain is enforced server-side; a INVITE_DOMAIN_MISMATCH is surfaced inline
 * using the server's detail (which names the accepted domain) rather than a
 * generic "invalid email".
 */
export function InviteMemberDialog({
  entityId,
  open,
  onClose,
  showSector = false,
}: InviteMemberDialogProps) {
  const invite = useInviteMember(entityId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteMemberFormValues>({
    resolver: zodResolver(inviteMemberFormSchema),
    defaultValues: { sectorId: '' },
  });

  const close = () => {
    reset();
    invite.reset();
    onClose();
  };

  const onSubmit = handleSubmit(({ fullName, email, sectorId }) => {
    // Sent only when the field is on and a sector was chosen, so every other
    // entity type's request is unchanged (the Backend refuses a sector there).
    const chosenSectorId = showSector ? toInviteSectorId(sectorId) : undefined;
    invite.mutate(
      {
        email,
        inviteeName: fullName,
        ...(chosenSectorId !== undefined && { sectorId: chosenSectorId }),
      },
      { onSuccess: close },
    );
  });

  // The server's `detail` is already specific (e.g. "Only acme.dev email
  // addresses can be invited to this team." for INVITE_DOMAIN_MISMATCH), so
  // surface it directly rather than mapping the code to a static string.
  // The two sector codes belong on the sector field, not the email field.
  const sectorErrorCode = invite.isError ? getApiErrorCode(invite.error) : undefined;
  const sectorError = sectorErrorCode
    ? (SECTOR_ERRORS as Record<string, string | undefined>)[sectorErrorCode]
    : undefined;
  const submitError =
    invite.isError && !sectorError
      ? (getApiErrorDetail(invite.error) ?? GENERIC_ERROR)
      : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Invite a Team Member"
      description="Invite a team member using their name and company email address. Only emails from your company domain are accepted."
      footer={
        <Button
          type="submit"
          form="invite-member-form"
          variant="green"
          disabled={invite.isPending}
          className="rounded-xl! inline-flex items-center justify-center gap-2 px-6 py-3 text-sm"
        >
          {invite.isPending ? 'Sending...' : 'Send Invite'}
          <ArrowRight size={16} aria-hidden="true" />
        </Button>
      }
    >
      <form
        id="invite-member-form"
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-3"
      >
        <Input
          label="Full name"
          placeholder="Jordan Lee"
          {...register('fullName')}
          error={errors.fullName?.message}
        />
        <Input
          label="Company email"
          type="email"
          placeholder="name@yourcompany.com"
          {...register('email')}
          error={errors.email?.message ?? submitError}
        />
        {showSector && (
          <InviteSectorField {...register('sectorId')} error={sectorError} />
        )}
        <p className="text-xs text-slate">
          An invitation email will be sent. The invite expires in 30 days.
        </p>
      </form>
    </Dialog>
  );
}
